'use client';
import { useTranslations } from 'next-intl';
import type { FC } from 'react';
import Reference2_0Tag from '@/app/assets/imgs/reference_2.0_tag.svg';
import { BaseLineUsed } from '@/app/components/icons/BaseLineUsed';
import HoverPlayMedia from '@/app/components/shared/HoverPlayMedia';
import { Button } from '@/app/components/ui/button';
import { MODEL_VERSION, SITE } from '@/app/constants';
import { useRecreate } from '@/app/hooks';
import type { PostReferenceCardProps, TaskInputPrompts } from '@/app/types';
import { formatCount, getAspectRatio } from '@/app/utils';

export const PostReferenceCard: FC<PostReferenceCardProps> = ({
  data,
  handleDetail,
  forceVideoAspectRatio,
  showTag = true,
}) => {
  const t = useTranslations();
  const { handleRecreate } = useRecreate();
  const creation = data.share?.creations?.[0];

  const useCount = data.share?.use_count || 0;
  const themeName = data.element?.name || '';
  const coverUri = creation?.cover_uri;
  const videoUri = creation?.uri;
  const isReference2_0 = data.share?.element_labels?.includes('2.0');
  const isCommercial = data.element?.pay_info?.pay_required ?? false;
  const isOfficialPartner = SITE === 'china' && data.share?.element_labels?.includes('official_partner');

  const handleClick = () => {
    handleDetail?.(data.element?.id || '', data.traceId);
  };

  const handleUseClick = (e: React.MouseEvent) => {
    e.stopPropagation();

    // 跳转到 q2-pro 参考生（character2video）
    const prompts = [
      {
        content: `[@${data.element?.name || ''}]`,
        type: 'text' as TaskInputPrompts['type'],
      },
      {
        content: data.element?.components?.map((it) => it.content),
        material: {
          id: data.element?.id || '',
          version: data.element?.version || '',
        },
        name: data.element?.name,
        type: 'material' as TaskInputPrompts['type'],
      },
    ] as TaskInputPrompts[];

    const modelVersion = data.element?.modality === 'video' ? MODEL_VERSION.Q2_PRO : MODEL_VERSION.Q2;

    handleRecreate({
      creationId: '',
      input: {
        enhance: true,
        prompts,
      },
      settings: {
        model_version: modelVersion,
        duration: 5,
        resolution: '1080p',
      },
      taskId: '',
      taskType: 'character2video',
      traceId: data.traceId,
    });
  };

  const aspectRatio = getAspectRatio({
    size: creation?.resolution,
    forceVideoAspectRatio,
  });

  return (
    <div
      className="group relative w-full cursor-pointer overflow-hidden rounded-8"
      onClick={handleClick}
      style={{
        aspectRatio,
      }}
    >
      {videoUri && coverUri && (
        <HoverPlayMedia
          id={data.element?.id || ''}
          uri={videoUri}
          coverUri={coverUri}
          className="h-full w-full"
          aspectRatio={aspectRatio}
          videoClassName={forceVideoAspectRatio ? 'object-contain' : 'object-cover'}
          borderRadiusClassName="rounded-8"
          eventName="post_reference_card_hover"
        />
      )}

      <div className="absolute top-2 left-2 z-10 flex items-center gap-2">
        {showTag && isReference2_0 && (
          <div className="flex h-5 items-center gap-[2px] rounded-4 bg-system-black48 px-2 backdrop-blur-[5px]">
            <img src={Reference2_0Tag.src} alt="reference_2.0_tag" className="h-3" />
            <span className="text-[10px] text-system-white">{t('elements.2_0_tag')}</span>
            <img src={Reference2_0Tag.src} alt="reference_2.0_tag" className="h-3 scale-x-[-1]" />
          </div>
        )}
      </div>

      <div className="absolute right-2 bottom-2 z-10 flex items-center gap-2">
        {useCount > 0 && (
          <div className="flex items-center gap-1 text-system-white">
            <BaseLineUsed className="size-5 sm:size-3" />
            <span className="text-sm sm:text-[10px]">{formatCount(useCount)}</span>
          </div>
        )}
      </div>

      <div className="pointer-events-none absolute inset-0 z-20 flex flex-col bg-system-black64 opacity-0 transition-opacity duration-200 group-hover:opacity-100 sm:hidden">
        {themeName && (
          <div className="flex flex-1 items-center justify-center text-center font-semibold text-system-white text-xl">
            {themeName}
          </div>
        )}

        <div className="flex h-20 justify-center">
          <Button
            onClick={handleUseClick}
            className="pointer-events-auto h-10 w-[186px] overflow-hidden rounded-12 bg-white text-black"
          >
            {t('elements.submit.dialog.form.use')}
            {isCommercial || isOfficialPartner ? (
              <div className="absolute top-0 right-0 flex h-[17px] items-center justify-center rounded-bl-8 bg-[#5765F2] px-1.5 font-medium text-[9px] text-system-white">
                {isCommercial ? (SITE === 'china' ? '官方授权限时免费' : t('elements.limitedFree')) : '官方合作'}
              </div>
            ) : null}
          </Button>
        </div>
      </div>
    </div>
  );
};
